import * as vp from 'vopay';
import * as Yup from 'yup';
import { useMemo } from 'react';

import {
  createCurrencyField,
  createPercentageField,
  createSelectField,
  createToggleField,
  validationMessages,
  createTextField,
} from './useFields.helpers';
import fieldUtilsFactory from './fieldUtils.factory';
import type { NewBillingPackageData } from '../../components/BillingPackages/types';
import type { DataType, FieldData, FieldUtils, SubSection } from './types';
import type { MultiStepFormCtx } from '../../providers/MultiStepForm.provider';

import { useModuleFeatureFlag } from '../../providers/FeatureFlag.provider';
import { PlanSettings, PseudoDataType } from 'vopay';
import { useTranslation } from 'react-i18next';

export type BillingPackageCreationSections = {
  packageInfo: SubSection<NewBillingPackageData>[];
  billingInfo: SubSection<NewBillingPackageData>[];
  subscriptionInfo: SubSection<NewBillingPackageData>[];
};

export type BillingPackageCreationSection = keyof BillingPackageCreationSections;
export type Ctx = MultiStepFormCtx<NewBillingPackageData>;

export const sections = (
  permissions: { [feature: string]: boolean },
  makeOptional: boolean,
  isAccountCreation: boolean,
  t: (
    key: string,
    options?: {
      [key: string]: string | number;
    },
  ) => string,
  planSettings?: PlanSettings,
): BillingPackageCreationSections => {
  const subscriptionInfo = createSubscriptionSection(planSettings || {});
  const billingInfoSubSections = createBillingPackageSubsections(subscriptionInfo, makeOptional);
  return {
    packageInfo: [
      {
        label: t('PackageInfo.title'),
        fields: {
          country: {
            label: t('PackageInfo.countryLabel'),
            placeholder: t('PackageInfo.countryPlaceholder'),
            fieldName: 'country',
            initValue: '',
            dataType: 'mapped',
            getInputState: () => 'required',
            showField: () => !isAccountCreation,
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx);
            },
            getSelectOptions: () => vp.CANADA_US_COUNTRY_OPTION,
            validate() {
              return Yup.string().required();
            },
          },
          autoBalanceTransferEnabled: {
            label: t('PackageInfo.autoBalanceTransferEnabledLabel'),
            placeholder: '',
            fieldName: 'autoBalanceTransferEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: () => 'optional',
            showField: () => true,
            getElement(ctx: Ctx) {
              return createToggleField(this, ctx);
            },
          },
          setupFee: {
            label: t('PackageInfo.implementationFeeLabel', {
              defaultValue: 'Implementation Fee',
            }),
            placeholder: t('PackageInfo.setupFeePlaceholder'),
            fieldName: 'setupFee',
            initValue: '',
            dataType: 'currency',
            getInputState: () => 'required',
            showField: () => true,
            getElement(ctx: Ctx) {
              return createCurrencyField(this, ctx);
            },
            validate() {
              return makeOptional ? Yup.string().notRequired() : Yup.string().required(validationMessages.fee);
            },
          },
        },
        showSection: () => true,
      },
    ],
    billingInfo: [
      {
        label: t('BillingInfo.title'),
        fields: {
          planType: {
            label: t('BillingInfo.planTypeLabel'),
            placeholder: permissions.canUsePayAsYouGo
              ? t('BillingInfo.planTypePayAsYouGoPlaceholder')
              : t('BillingInfo.planTypePlaceholder'),
            fieldName: 'planType',
            initValue: permissions.canUsePayAsYouGo || (isAccountCreation && makeOptional) ? '' : 'subscription',
            dataType: 'mapped',
            getInputState: () => (permissions.canUsePayAsYouGo ? 'required' : 'disabled'),
            showField: () => true,
            getSelectOptions: () => ({
              subscription: t('BillingInfo.planTypeSubscriptionOption'),
              'pay as you go': t('BillingInfo.planTypePayAsYouGoOption'),
            }),
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx);
            },
            validate() {
              return makeOptional ? Yup.string().notRequired() : Yup.string().required();
            },
          },
          billingMethod: {
            label: t('BillingInfo.billingMethodLabel'),
            placeholder: t('BillingInfo.billingMethodPlaceholder'),
            fieldName: 'billingMethod',
            initValue: '',
            dataType: 'mapped',
            getInputState: () => 'required',
            showField: () => true,
            getSelectOptions: () => ({
              'post paid': t('BillingInfo.billingMethodPostPaidOption'),
              'per transaction': t('BillingInfo.billingMethodPerTransactionOption'),
            }),
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx);
            },
            validate() {
              return makeOptional ? Yup.string().notRequired() : Yup.string().required();
            },
          },
          currency: {
            label: t('BillingInfo.currencyLabel'),
            placeholder: t('BillingInfo.currencyPlaceholder'),
            fieldName: 'currency',
            initValue: '',
            dataType: 'mapped',
            getInputState: () => 'required',
            showField: () => true,
            getSelectOptions: (ctx: Ctx) => (isCountry(ctx, 'US') ? vp.US_CURRENCY_ONLY : vp.CURRENCY_SELECT_OPTIONS),
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx);
            },
            validate() {
              return makeOptional ? Yup.string().notRequired() : Yup.string().required();
            },
          },
          invoiceDue: {
            label: t('BillingInfo.invoiceDueLabel'),
            placeholder: t('BillingInfo.invoiceDuePlaceholder'),
            fieldName: 'invoiceDue',
            initValue: '',
            dataType: 'mapped',
            getInputState: () => 'required',
            showField: () => true,
            getSelectOptions: () => ({
              beginning: t('BillingInfo.invoiceDueBeginningOption'),
              end: t('BillingInfo.invoiceDueEndOption'),
              specific: t('BillingInfo.invoiceDueSpecificOption'),
            }),
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx);
            },
            validate() {
              return makeOptional ? Yup.string().notRequired() : Yup.string().required();
            },
          },
          invoiceDueDate: {
            label: t('BillingInfo.invoiceDueDateLabel'),
            placeholder: t('BillingInfo.invoiceDueDatePlaceholder'),
            fieldName: 'invoiceDueDate',
            initValue: '',
            dataType: 'number',
            getInputState: () => 'required',
            showField: (ctx: Ctx) => ctx.values.invoiceDue === 'specific',
            getSelectOptions: () =>
              Array.from({ length: 31 }, (_, k) => k + 1).reduce((acc, k) => ({ ...acc, [k]: `Day ${k}` }), {}),
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx);
            },
            validate(ctx: Ctx) {
              return makeOptional
                ? Yup.string().notRequired()
                : this.showField(ctx)
                  ? Yup.string().required()
                  : Yup.string().notRequired();
            },
          },
          termInMonths: {
            label: t('BillingInfo.termInMonthsLabel'),
            placeholder: t('BillingInfo.termInMonthsPlaceholder'),
            initValue: '',
            fieldName: 'termInMonths',
            dataType: 'mapped',
            showField: () => true,
            getInputState: () => 'required',
            getSelectOptions: () => vp.TERM_OPTIONS,
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx);
            },
            validate() {
              return makeOptional ? Yup.string().notRequired() : Yup.string().required();
            },
          },
          gcmEnabled: {
            label: t('BillingInfo.gcmEnabledLabel'),
            placeholder: '',
            fieldName: 'gcmEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: () => 'optional',
            showField: () => true,
            getElement(ctx: Ctx) {
              return createToggleField(this, ctx);
            },
          },
          subAccountsEnabled: {
            label: t('BillingInfo.subAccountsEnabledLabel'),
            placeholder: '',
            fieldName: 'subAccountsEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: () => 'disabled',
            showField: () => true,
            getElement(ctx: Ctx) {
              return createToggleField(this, ctx);
            },
          },
          clientAccountsEnabled: {
            label: t('BillingInfo.clientAccountsEnabledLabel'),
            placeholder: '',
            fieldName: 'clientAccountsEnabled',
            initValue: false,
            dataType: 'boolean',
            getInputState: () => 'disabled',
            showField: () => true,
            getElement(ctx: Ctx) {
              return createToggleField(this, ctx);
            },
          },
          maxGcmAmount: {
            label: t('BillingInfo.maxGcmAmountLabel'),
            placeholder: '10,000.00',
            fieldName: 'maxGcmAmount',
            initValue: '',
            dataType: 'currency',
            getInputState: () => 'required',
            showField: () => true,
            getElement(ctx: Ctx) {
              return createCurrencyField(this, ctx);
            },
            validate() {
              return makeOptional ? Yup.string().notRequired() : Yup.string().required(validationMessages.maxAmount);
            },
          },
          platformLicenseFee: {
            label: t('BillingInfo.platformLicenseFeeLabel'),
            placeholder: t('BillingInfo.platformLicenseFeePlaceholder'),
            fieldName: 'platformLicenseFee',
            initValue: '',
            dataType: 'currency',
            getInputState: () => 'required',
            showField: () => true,
            getElement(ctx: Ctx) {
              return createCurrencyField(this, ctx);
            },
            validate() {
              return makeOptional ? Yup.string().notRequired() : Yup.string().required(validationMessages.fee);
            },
          },
          platformLicenseFeeFrequency: {
            label: t('BillingInfo.platformLicenseFeeFrequencyLabel'),
            placeholder: t('BillingInfo.platformLicenseFeeFrequencyPlaceholder'),
            fieldName: 'platformLicenseFeeFrequency',
            initValue: '',
            dataType: 'mapped',
            getInputState: () => 'optional',
            showField: () => true,
            getSelectOptions: () => ({
              monthly: t('BillingInfo.platformLicenseFeeFrequencyMonthlyOption'),
              yearly: t('BillingInfo.platformLicenseFeeFrequencyYearlyOption'),
            }),
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx);
            },
            validate() {
              return Yup.string().notRequired();
            },
          },
          noPlatformLicenseFee: {
            label: t('BillingInfo.noPlatformLicenseFeeLabel'),
            placeholder: t('BillingInfo.noPlatformLicenseFeePlaceholder'),
            fieldName: 'noPlatformLicenseFee',
            initValue: '',
            dataType: 'mapped',
            getInputState: () => 'required',
            showField: () => true,
            getSelectOptions: () => ({
              0: t('BillingInfo.noPlatformLicenseFeeOption', { count: 0 }),
              1: t('BillingInfo.noPlatformLicenseFeeOption', { count: 1 }),
              2: t('BillingInfo.noPlatformLicenseFeeOption', { count: 2 }),
              3: t('BillingInfo.noPlatformLicenseFeeOption', { count: 3 }),
              4: t('BillingInfo.noPlatformLicenseFeeOption', { count: 4 }),
              5: t('BillingInfo.noPlatformLicenseFeeOption', { count: 5 }),
              6: t('BillingInfo.noPlatformLicenseFeeOption', { count: 6 }),
            }),
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx);
            },
            validate() {
              return makeOptional ? Yup.string().notRequired() : Yup.string().required('Please select a time period');
            },
          },
          minProgramFee: {
            label: t('BillingInfo.minProgramFeeLabel'),
            placeholder: t('BillingInfo.minProgramFeePlaceholder'),
            fieldName: 'minProgramFee',
            initValue: '',
            dataType: 'currency',
            getInputState: () => 'required',
            showField: () => true,
            getElement(ctx: Ctx) {
              return createCurrencyField(this, ctx);
            },
            validate() {
              return makeOptional ? Yup.string().notRequired() : Yup.string().required(validationMessages.fee);
            },
          },
          noMinProgramFeePeriod: {
            label: t('BillingInfo.noMinProgramFeePeriodLabel'),
            placeholder: t('BillingInfo.noMinProgramFeePeriodPlaceholder'),
            fieldName: 'noMinProgramFeePeriod',
            initValue: '',
            dataType: 'mapped',
            getInputState: () => 'required',
            showField: () => true,
            getSelectOptions: () => ({
              0: t('BillingInfo.noMinProgramFeePeriodOption', { count: 0 }),
              1: t('BillingInfo.noMinProgramFeePeriodOption', { count: 1 }),
              2: t('BillingInfo.noMinProgramFeePeriodOption', { count: 2 }),
              3: t('BillingInfo.noMinProgramFeePeriodOption', { count: 3 }),
              4: t('BillingInfo.noMinProgramFeePeriodOption', { count: 4 }),
              5: t('BillingInfo.noMinProgramFeePeriodOption', { count: 5 }),
              6: t('BillingInfo.noMinProgramFeePeriodOption', { count: 6 }),
            }),
            getElement(ctx: Ctx) {
              return createSelectField(this, ctx);
            },
            validate() {
              return makeOptional ? Yup.string().notRequired() : Yup.string().required();
            },
          },
          reserveToActivate: {
            label: t('BillingInfo.reserveToActivateLabel'),
            placeholder: t('BillingInfo.reserveToActivatePlaceholder'),
            fieldName: 'reserveToActivate',
            initValue: '',
            dataType: 'currency',
            getInputState: () => (isAccountCreation ? 'required' : 'optional'),
            showField: () => isAccountCreation,
            getElement(ctx: Ctx) {
              return createCurrencyField(this, ctx);
            },
            validate() {
              return isAccountCreation && !makeOptional
                ? Yup.string().required(validationMessages.reserveToActivate)
                : Yup.string().notRequired();
            },
          },
        },
        showSection: () => true,
      },
    ],
    subscriptionInfo: [...billingInfoSubSections],
  };
};
const useBillingPackageCreationFields = (planSettings?: PlanSettings): FieldUtils<NewBillingPackageData> => {
  const { t } = useTranslation('translations', { keyPrefix: 'Hooks.UseFields.UseBillingPackageCreationFields' });
  const permissions = useModuleFeatureFlag('billingPackages');
  return useMemo(
    () => fieldUtilsFactory<NewBillingPackageData>(sections(permissions, false, false, t, planSettings)),
    [planSettings],
  );
};

function isProgressivePricing(ctx: Ctx) {
  return ctx.values.useProgressivePricing;
}

function isPayAsYouGo(ctx: Ctx) {
  return ctx.values.planType === 'pay as you go';
}

function isSubscription(ctx: Ctx) {
  return ctx.values.planType === 'subscription';
}

function isCountry(ctx: Ctx, country: string) {
  return ctx.values.country === country;
}

type SubscriptionField = {
  fieldName: string;
  label: string;
  value: string | boolean | null;
  type: vp.PseudoDataType;
  category: 'common' | 'maxAmounts' | 'tiers' | 'payAsYouGo' | 'permissions' | 'subscription';
  options?: vp.TierOption<string>[];
};

type SubscriptionSection = {
  serviceName: string;
  fields: SubscriptionField[];
};
function splitIntoWords(input: string): string {
  const knownWords = ['EFT', 'ACH', 'iQ11', 'IQ11'];

  // Check if input contains any known word and mark them
  for (const word of knownWords) {
    input = input.replace(new RegExp(word, 'g'), `|${word}|`);
  }

  // Split by known words (using the `|` as marker), or camelCase boundaries
  const parts = input.split('|').flatMap((part) => part.match(/[A-Z]?[a-z]+|[A-Z]+(?![a-z])|\d+/g) ?? []);

  return parts.filter(Boolean).join(' ');
}
export function createSubscriptionSection(planSettings: PlanSettings): SubscriptionSection[] {
  const sections: SubscriptionSection[] = [];

  for (const [serviceName, serviceDetails] of Object.entries(planSettings)) {
    const { common, maxAmounts, tiers, payAsYouGo, permissions, subscription } = serviceDetails;
    const fields: SubscriptionField[] = [];
    if (permissions) {
      for (const [fieldName, fieldDetails] of Object.entries(permissions)) {
        const details = fieldDetails;
        fields.push({
          fieldName,
          label: details.label || splitIntoWords(fieldName),
          value: details.value ?? null,
          type: details.type || 'unknown',
          category: 'permissions',
        });
      }
    }
    if (common) {
      for (const [fieldName, fieldDetails] of Object.entries(common)) {
        const details = fieldDetails;
        fields.push({
          fieldName,
          label: details.label || splitIntoWords(fieldName),
          value: details.value ?? null,
          type: details.type || 'unknown',
          category: 'common',
        });
      }
    }
    if (subscription) {
      for (const [fieldName, fieldDetails] of Object.entries(subscription)) {
        const details = fieldDetails;
        fields.push({
          fieldName,
          label: details.label || splitIntoWords(fieldName),
          value: details.value ?? null,
          type: details.type || 'unknown',
          category: 'subscription',
          options: details.options ? Object.values(details.options) : undefined,
        });
      }
    }
    if (maxAmounts) {
      for (const [fieldName, fieldDetails] of Object.entries(maxAmounts)) {
        const details = fieldDetails;
        fields.push({
          fieldName,
          label: details.label || splitIntoWords(fieldName),
          value: details.value ?? null,
          type: details.type || 'unknown',
          category: 'maxAmounts',
        });
      }
    }
    if (tiers) {
      for (const [fieldName, fieldDetails] of Object.entries(tiers)) {
        const details = fieldDetails;
        fields.push({
          fieldName,
          label: details.label || splitIntoWords(fieldName),
          value: details.value ?? null,
          type: details.type || 'unknown',
          options: Object.values(details.options) || [],
          category: 'tiers',
        });
      }
    }
    if (payAsYouGo) {
      for (const [fieldName, fieldDetails] of Object.entries(payAsYouGo)) {
        const details = fieldDetails;
        fields.push({
          fieldName,
          label: details.label || splitIntoWords(fieldName),
          value: details.value ?? null,
          type: details.type || 'unknown',
          category: 'payAsYouGo',
        });
      }
    }
    if (fields.length > 0) {
      sections.push({ serviceName, fields });
    }
  }

  return sections;
}

function checkIfEnabled(ctx: Ctx, fields: string[]): boolean {
  if (fields.length === 0) return true;
  return fields.some((key) => !!ctx.values[key]);
}

function convertPseudoToDataType(pseudo: PseudoDataType): DataType {
  const map: Record<PseudoDataType, DataType> = {
    boolean: 'boolean',
    amount: 'currency',
    percent: 'percentage',
    select: 'mapped',
  };

  return map[pseudo] ?? 'text';
}
const optionalServiceSection = ['Branding', 'eLinx', 'IQ11 Token', 'IQ11 Third Party'];
function getBillingPackageFields(
  field: SubscriptionField,
  sectionFields: SubscriptionField[],
  serviceName: string,
  makeOptional: boolean = false,
): FieldData<NewBillingPackageData> {
  const permissionFields = sectionFields.filter((f) => f.category === 'permissions').map((f) => f.fieldName);
  const fieldDataType = convertPseudoToDataType(field.type);
  return {
    label: field.label,
    placeholder: field.category === 'tiers' ? 'No Tier found' : '',
    fieldName: field.fieldName, // abstract to any string
    initValue: fieldDataType === 'boolean' ? false : '',
    dataType: fieldDataType, // convert types
    getInputState: () => (optionalServiceSection.includes(serviceName) ? 'optional' : 'required'),
    showField(ctx: Ctx) {
      if (field.category === 'permissions') {
        return true;
      } else if (field.category === 'payAsYouGo') {
        return isPayAsYouGo(ctx) && checkIfEnabled(ctx, permissionFields);
      } else if (field.category === 'tiers') {
        return isSubscription(ctx) && checkIfEnabled(ctx, permissionFields) && isProgressivePricing(ctx);
      } else if (field.category === 'maxAmounts' || field.category === 'common') {
        return checkIfEnabled(ctx, permissionFields);
      } else if (field.category === 'subscription') {
        return isSubscription(ctx) && checkIfEnabled(ctx, permissionFields) && !isProgressivePricing(ctx);
      }
      return checkIfEnabled(ctx, permissionFields);
    },

    getElement(ctx: Ctx) {
      if (fieldDataType === 'boolean') {
        return createToggleField(this, ctx);
      } else if (fieldDataType === 'currency') {
        return createCurrencyField(this, ctx);
      } else if (fieldDataType === 'percentage') {
        return createPercentageField(this, ctx);
      } else if (fieldDataType === 'mapped') {
        return createSelectField(this, ctx, { noOrdering: true });
      }
      return createTextField(this, ctx);
    },
    serializeValue:
      fieldDataType === 'currency' || fieldDataType === 'percentage'
        ? (value: NewBillingPackageData[keyof NewBillingPackageData]) => {
            if (value === undefined) {
              return undefined;
            }
            return vp.sanitizeToNumeric(value.toString() || '');
          }
        : undefined,
    getSelectOptions: field.options
      ? () => {
          const optionsArray = field.options as vp.TierOption<string>[];

          return optionsArray.reduce(
            (acc, option) => {
              acc[option.value] = option.label;
              return acc;
            },
            {} as Record<string, string>,
          );
        }
      : undefined,
    validate(ctx: Ctx) {
      return this.showField(ctx) &&
        field.category !== 'permissions' &&
        !optionalServiceSection.includes(serviceName) &&
        !makeOptional
        ? Yup.string().required()
        : Yup.string().notRequired();
    },
    resetOnUpdate: field.category === 'permissions' ? ['currency'] : ['planType', ...permissionFields], // fix typing
  };
}
function createBillingPackageSubsections(
  subscriptionSections: SubscriptionSection[],
  makeOptional: boolean,
): SubSection<NewBillingPackageData>[] {
  return subscriptionSections.map(
    (section) =>
      ({
        label: section.serviceName,
        fields: {
          ...section.fields.reduce(
            (acc, field) => {
              acc[field.fieldName] = getBillingPackageFields(field, section.fields, section.serviceName, makeOptional);
              return acc;
            },
            {} as Record<string, FieldData<NewBillingPackageData>>,
          ),
        },
        showSection() {
          return true;
        },
      }) as SubSection<NewBillingPackageData>,
  );
}

export default useBillingPackageCreationFields;
